import React, {useLayoutEffect, useRef, useState} from 'react';
import {Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography} from '@mui/material';
import {
    BlurOn, Check, Close, DeleteOutline, FileUpload, Mic, MicOff, NotInterested, Videocam, VideocamOff, Wallpaper,
} from '@mui/icons-material';

const PLAYER_AVATAR_BASE = 'https://dragondropvtt-player-avatars-public.s3.us-west-1.amazonaws.com/';

// Docked height. Two values rather than one so an open-but-not-joined dock isn't a
// tall empty band across the top of the map. TableTop pushes its top-anchored
// chrome (toolbars, encounter/view buttons, toasts) down by exactly this much —
// see videoDockHeight() — so the dock and the offset can never drift apart.
const TILE_H_PX = 96;

export const VIDEO_DOCK_HEIGHT_IDLE = 52;
export const VIDEO_DOCK_HEIGHT_JOINED = 116;

// The dock's bottom border continues the active sidebar item's underline, so both are
// drawn with this one style.
export const NAV_ACTIVE_LINE = '2px solid var(--th-6c4f9b)';

// Joined needs the 96px tiles, 4px padding each side and the 2px border; below this the
// tiles would be clipped, so the dock stops aligning and uses its own height instead.
const VIDEO_DOCK_MIN_JOINED = TILE_H_PX + 2 * 4 + 2;

// `navHeight` is the measured bottom edge of the active sidebar item (see
// useNavAlignedDockHeight), or null when there's nothing to align to. Both states use it,
// so the dock's bottom border stays level with that item's underline whether or not
// you've joined.
export function videoDockHeight(show, joined, navHeight = null) {
    if (!show) return 0;
    if (joined) return navHeight && navHeight >= VIDEO_DOCK_MIN_JOINED ? navHeight : VIDEO_DOCK_HEIGHT_JOINED;
    return navHeight ?? VIDEO_DOCK_HEIGHT_IDLE;
}

// Distance from the top of the dock's containing block to the bottom edge of the active
// sidebar item (Tabletop, on this page - the second item, below Tavern), so the dock's
// border reads as one line with that item's underline. Measured rather than hard-coded:
// it depends on icon line boxes and the font-scale setting, and is fractional (111.44px
// at default scale). `anchorRef` is any element sharing the dock's containing block (the
// dock sits at top: 0 of its parent). Null when there's nothing sensible to align to.
export function useNavAlignedDockHeight(anchorRef, enabled) {
    const [height, setHeight] = useState(null);

    useLayoutEffect(() => {
        if (!enabled) return undefined;
        const measure = () => {
            const link = document.querySelector('[data-sidenav] a.active');
            const container = anchorRef.current?.parentElement;
            if (!link || !container) { setHeight(null); return; }
            const containerTop = container.getBoundingClientRect().top + container.clientTop;
            // Not rounded: a half-pixel off is visible as a step where the two lines meet.
            const h = link.getBoundingClientRect().bottom - containerTop;
            // Outside this range the sidebar isn't beside the dock (e.g. a narrow layout,
            // or the page scrolled), so don't stretch the dock to match it.
            setHeight(h >= 32 && h <= 240 ? h : null);
        };
        measure();
        const observer = new ResizeObserver(measure);
        const link = document.querySelector('[data-sidenav] a.active');
        if (link) observer.observe(link);
        if (anchorRef.current?.parentElement) observer.observe(anchorRef.current.parentElement);
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [anchorRef, enabled]);

    return height;
}

const TILE_W = 128;
const TILE_H = TILE_H_PX;

// Keeps the centred tile row clear of the right-hand control cluster (mute, camera,
// background, close ≈ 150px) with a little breathing room, so a full party scrolls inside
// the row rather than sliding under the buttons. Joined adds the "N in call" chip
// (≈ 90px) to that cluster, so the row gives up more room there.
const CONTENT_MAX_WIDTH = 'calc(100% - 200px)';
const JOINED_CONTENT_MAX_WIDTH = 'calc(100% - 300px)';

function characterForUsername(partyCharacters, username) {
    return (partyCharacters || []).find(c => c.uploadedByUser === username) || null;
}

function avatarUrlForCharacter(character) {
    return character?.objectKey ? PLAYER_AVATAR_BASE + character.objectKey : null;
}

const MEDIA_ERROR_MESSAGES = {
    InsecureContextError: "Camera/mic access needs HTTPS (or localhost). If you're testing from another device, open this app over https:// or via localhost - a plain http:// LAN address won't work.",
    NotAllowedError: 'Camera/mic permission was denied. Click the lock/camera icon in your address bar, allow camera & microphone for this site, then try again.',
    PermissionDeniedError: 'Camera/mic permission was denied. Click the lock/camera icon in your address bar, allow camera & microphone for this site, then try again.',
    NotFoundError: 'No camera or microphone was found on this device.',
    DevicesNotFoundError: 'No camera or microphone was found on this device.',
    NotReadableError: 'Your camera or mic is already in use by another app (or another browser tab). Close it and try again.',
    TrackStartError: 'Your camera or mic is already in use by another app (or another browser tab). Close it and try again.',
    SecurityError: "Camera/mic access needs HTTPS (or localhost). If you're testing from another device, open this app over https:// or via localhost.",
};

function mediaErrorMessage(err) {
    return MEDIA_ERROR_MESSAGES[err?.name] || "Couldn't access camera/mic. Check browser permissions and try again.";
}

// A single video tile: shows the live stream, or an avatar/initial placeholder
// when the camera is off (a disabled video track otherwise just freezes on the
// last frame, which reads as broken rather than intentionally off).
function VideoTile({stream, muted, cameraOff, label, avatarUrl, isLocal}) {
    const videoRef = useRef(null);
    React.useEffect(() => {
        if (videoRef.current) videoRef.current.srcObject = stream || null;
    }, [stream]);

    return (
        <Box sx={{
            position: 'relative', width: TILE_W, height: TILE_H, borderRadius: 1, overflow: 'hidden',
            backgroundColor: 'var(--lm-bg-0d0f14)', border: '1px solid rgba(var(--th-6c4f9b-rgb), 0.5)', flexShrink: 0,
        }}>
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={isLocal}
                style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    display: cameraOff ? 'none' : 'block',
                    transform: isLocal ? 'scaleX(-1)' : 'none',
                }}
            />
            {cameraOff && (
                <Box sx={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'var(--lm-bg-th-2c2433)',
                }}>
                    {avatarUrl
                        ? <img src={avatarUrl} alt={label} style={{width: 44, height: 44, borderRadius: '50%', objectFit: 'cover'}}/>
                        : (
                            <Box sx={{
                                width: 44, height: 44, borderRadius: '50%', backgroundColor: 'var(--th-6c4f9b)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#F5F5F5', fontSize: 'calc(1.1rem * var(--tt-font-scale, 1))', fontWeight: 700,
                            }}>
                                {label?.[0]?.toUpperCase() ?? '?'}
                            </Box>
                        )}
                </Box>
            )}
            <Box sx={{
                position: 'absolute', left: 4, bottom: 4, right: 4, display: 'flex',
                alignItems: 'center', justifyContent: 'space-between', gap: 0.5,
            }}>
                <Typography sx={{
                    fontSize: 'calc(0.65rem * var(--tt-font-scale, 1))', color: 'var(--lm-tx-f5f5f5)', backgroundColor: 'rgba(0,0,0,0.55)',
                    px: 0.5, borderRadius: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {label}{isLocal ? ' (You)' : ''}
                </Typography>
                {muted && <MicOff sx={{fontSize: 14, color: 'var(--lm-tx-e53935)', backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: '50%', p: 0.25}}/>}
            </Box>
        </Box>
    );
}

// "● N in call", with the names in its tooltip (character names where known, as on the tiles).
function InCallChip({names, sx}) {
    return (
        <Tooltip title={names.join(', ')}>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, flexShrink: 0,
                borderRadius: 1, border: '1px solid rgba(var(--th-6c4f9b-rgb), 0.5)',
                backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.15)', whiteSpace: 'nowrap', cursor: 'default',
                ...sx,
            }}>
                <Box sx={{width: 7, height: 7, borderRadius: '50%', backgroundColor: '#4CAF50', flexShrink: 0}}/>
                <Typography sx={{fontSize: 'calc(0.72rem * var(--tt-font-scale, 1))', color: 'var(--lm-tx-th-c9a0f0)', fontWeight: 600}}>
                    {names.length} in call
                </Typography>
            </Box>
        </Tooltip>
    );
}

const MENU_TEXT_SX = {fontSize: 'calc(0.8rem * var(--tt-font-scale, 1))'};

// What's behind you in your own feed (and so what peers see): nothing, a blur, or an
// image you've uploaded. The image is remembered on this device (see useWebRTCMesh).
function BackgroundMenu({effect, loading, image, onSelect, onUpload, onRemove}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const fileInputRef = useRef(null);
    const close = () => setAnchorEl(null);

    function choose(next) {
        close();
        if (next !== effect) onSelect(next);
    }

    function handleFile(e) {
        const file = e.target.files?.[0];
        e.target.value = ''; // so picking the same file again still fires onChange
        if (file) onUpload(file);
    }

    const option = (value, icon, label, disabled = false) => (
        <MenuItem onClick={() => choose(value)} disabled={disabled} selected={effect === value}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primaryTypographyProps={{sx: MENU_TEXT_SX}}>{label}</ListItemText>
            {effect === value && <Check fontSize="small" sx={{ml: 1.5}}/>}
        </MenuItem>
    );

    return (
        <>
            <Tooltip title={loading ? 'Loading background...' : 'Background'}>
                <span>
                    <IconButton size="small" disabled={loading} onClick={e => setAnchorEl(e.currentTarget)}
                                sx={{color: effect !== 'none' ? 'var(--lm-tx-th-c9a0f0)' : 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a60))'}}>
                        <Wallpaper fontSize="small"/>
                    </IconButton>
                </span>
            </Tooltip>
            <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFile}/>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}
                  anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                  transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                {option('none', <NotInterested fontSize="small"/>, 'No background')}
                {option('blur', <BlurOn fontSize="small"/>, 'Blur')}
                {image && option('image', (
                    <Box component="img" src={image} alt=""
                         sx={{width: 20, height: 15, objectFit: 'cover', borderRadius: 0.5}}/>
                ), 'My image')}
                <Divider/>
                <MenuItem onClick={() => { close(); fileInputRef.current?.click(); }}>
                    <ListItemIcon><FileUpload fontSize="small"/></ListItemIcon>
                    <ListItemText primaryTypographyProps={{sx: MENU_TEXT_SX}}>
                        {image ? 'Replace image...' : 'Upload image...'}
                    </ListItemText>
                </MenuItem>
                {image && (
                    <MenuItem onClick={() => { close(); onRemove(); }}>
                        <ListItemIcon><DeleteOutline fontSize="small"/></ListItemIcon>
                        <ListItemText primaryTypographyProps={{sx: MENU_TEXT_SX}}>Remove image</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}

// Docked directly under the app header, spanning the map area. Deliberately does
// NOT overlap the right side panel, which stays full height beside it.
function VideoChatPanel({videoMesh, partyCharacters, myUsername, rightOffset = 0, navHeight = null, zIndex = 150, onClose}) {
    const {
        localStream, joined, participants, localMuted, localCameraOff, mediaError,
        backgroundEffect, backgroundLoading, backgroundError, backgroundImage, roster = [], presenceChecked = false,
        join, leave, toggleMute, toggleCamera, setBackgroundEffect, uploadBackgroundImage, removeBackgroundImage,
    } = videoMesh;

    function handleClose() {
        leave();
        onClose?.();
    }

    const myCharacter = characterForUsername(partyCharacters, myUsername);
    const myLabel = myCharacter?.characterName || myUsername;
    const nameFor = u => characterForUsername(partyCharacters, u)?.characterName || u;
    const inCallNames = [`${myLabel} (You)`, ...Object.keys(participants).map(nameFor)];
    // Before joining: everyone who answered the presence check (see useWebRTCMesh).
    const rosterNames = roster.map(nameFor);

    return (
        <Box
            onMouseDown={e => e.stopPropagation()}
            sx={{
                position: 'absolute', top: 0, left: 0, right: `${rightOffset}px`, zIndex,
                height: videoDockHeight(true, joined, navHeight),
                // border-box so the border's bottom edge is the dock's bottom edge, level
                // with the active sidebar item's own bottom border.
                boxSizing: 'border-box',
                // Left-aligned by default so the pre-join state doesn't drift sideways
                // every time the side tab panel expands or collapses (the dock's right
                // edge tracks the panel, so anything centred here moves with it). The
                // joined tile row opts back into centring with its own auto margins.
                display: 'flex', alignItems: 'center',
                // 4px vertical padding when joined: the aligned height (~111px) leaves
                // only ~15px around the 96px tiles and border. The tiles are centred, so
                // any extra height splits evenly above and below.
                px: 1.5, py: joined ? 0.5 : 1,
                backgroundColor: 'var(--lm-bg-1a1f2b)',
                // Same line as the active sidebar item, and no drop shadow, which would
                // blur it into a softer band than the sidebar's crisp underline.
                borderBottom: NAV_ACTIVE_LINE,
            }}
        >
            {!joined ? (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, maxWidth: CONTENT_MAX_WIDTH, minWidth: 0}}>
                    <Box
                        onClick={join}
                        sx={{
                            flexShrink: 0, px: 2, py: 0.5, borderRadius: 1, cursor: 'pointer',
                            backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.25)', border: '1px solid var(--th-6c4f9b)', color: 'var(--lm-tx-th-c9a0f0)',
                            fontSize: 'calc(0.8rem * var(--tt-font-scale, 1))', '&:hover': {backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.4)'},
                        }}
                    >
                        Join Video Chat
                    </Box>
                    {roster.length > 0 ? (
                        <InCallChip names={rosterNames}/>
                    ) : presenceChecked && (
                        <Typography sx={{fontSize: 'calc(0.72rem * var(--tt-font-scale, 1))', color: 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a45))', whiteSpace: 'nowrap'}}>
                            No one's in the call yet
                        </Typography>
                    )}
                    {mediaError && (
                        <Typography sx={{
                            fontSize: 'calc(0.72rem * var(--tt-font-scale, 1))', color: 'var(--lm-tx-e53935)', minWidth: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }} title={mediaErrorMessage(mediaError)}>
                            {mediaErrorMessage(mediaError)}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box sx={{
                    // Auto margins centre the row in the bar (the controls sit outside the
                    // flex flow, so they don't pull it off-centre).
                    mx: 'auto',
                    maxWidth: JOINED_CONTENT_MAX_WIDTH, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1,
                    // 'safe' keeps the first tile reachable once the row overflows and
                    // starts scrolling; plain 'center' would clip it past the scroll origin.
                    justifyContent: 'safe center',
                    overflowX: 'auto', overflowY: 'hidden',
                    '&::-webkit-scrollbar': {height: 6},
                    '&::-webkit-scrollbar-thumb': {backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.6)', borderRadius: 3},
                }}>
                    <VideoTile
                        stream={localStream} muted={localMuted} cameraOff={localCameraOff}
                        label={myLabel} avatarUrl={avatarUrlForCharacter(myCharacter)} isLocal
                    />
                    {Object.entries(participants).map(([username, p]) => {
                        const character = characterForUsername(partyCharacters, username);
                        return (
                            <VideoTile
                                key={username}
                                stream={p.stream} muted={p.muted} cameraOff={p.cameraOff || !p.stream}
                                label={character?.characterName || username}
                                avatarUrl={avatarUrlForCharacter(character)}
                            />
                        );
                    })}
                </Box>
            )}

            <Box sx={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                display: 'flex', alignItems: 'center',
            }}>
                {joined && (
                    <>
                        {/* You plus every connected peer. */}
                        <InCallChip names={inCallNames} sx={{mr: 1}}/>
                        {backgroundError && (
                            <Tooltip title={backgroundError.userFacing
                                ? backgroundError.message
                                : "Couldn't start the video background. Check your connection and try again."}>
                                <Typography sx={{fontSize: 'calc(0.7rem * var(--tt-font-scale, 1))', color: 'var(--lm-tx-e53935)', mr: 1, whiteSpace: 'nowrap'}}>
                                    Background failed
                                </Typography>
                            </Tooltip>
                        )}
                        <Tooltip title={localMuted ? 'Unmute' : 'Mute'}>
                            <IconButton size="small" onClick={toggleMute}
                                        sx={{color: localMuted ? 'var(--lm-tx-e53935)' : 'var(--lm-tx-th-c9a0f0)'}}>
                                {localMuted ? <MicOff fontSize="small"/> : <Mic fontSize="small"/>}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={localCameraOff ? 'Turn camera on' : 'Turn camera off'}>
                            <IconButton size="small" onClick={toggleCamera}
                                        sx={{color: localCameraOff ? 'var(--lm-tx-e53935)' : 'var(--lm-tx-th-c9a0f0)'}}>
                                {localCameraOff ? <VideocamOff fontSize="small"/> : <Videocam fontSize="small"/>}
                            </IconButton>
                        </Tooltip>
                        <BackgroundMenu
                            effect={backgroundEffect} loading={backgroundLoading} image={backgroundImage}
                            onSelect={setBackgroundEffect} onUpload={uploadBackgroundImage}
                            onRemove={removeBackgroundImage}
                        />
                    </>
                )}
                <Tooltip title="Close video chat">
                    <IconButton size="small" onClick={handleClose} sx={{color: 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a60))'}}>
                        <Close fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}

export default VideoChatPanel;
